import { useEffect } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import type { FileTypeId, UndecoratedFile } from '@witmetrics/utils';
import { useDispatch, useFile } from '@witmetrics/data-store';
import { isEmpty } from '@witmetrics/utils/misc';
import { API } from '@/api';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { useId } from '@/hooks/useId';
import { useString } from '@/hooks/useString';
import { useInvalidFields } from '@/hooks/useInvalidFields';
import { showSuccessToaster } from '@/utils/toasters';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import TextField from '@/components/inputs/TextField';
import {
  classes,
  formatExtension,
  isDisabled,
  splitName,
  validateFields,
} from './utils';

type FileInfoDialogProps = {
  fileId: number;
  onSave?: (file: UndecoratedFile) => void;
  onClose: () => void;
};

export default function FileInfoDialog({
  fileId,
  onSave,
  onClose,
}: FileInfoDialogProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const file = useFile(fileId);
  const [name, setName] = useString();
  const [extension, setExtension] = useString();
  const [fileTypeId, setFileTypeId] = useId<FileTypeId>();
  const [description, setDescription] = useString('');
  const { setInvalidFields, isValidField } = useInvalidFields();
  const [isSaving, toggleSaving] = useToggle();

  useEffect(() => {
    if (file) fillFields();
  }, [file]);

  const fillFields = () => {
    if (!file) return;
    const parts = splitName(file.name);
    setName(parts.name);
    setExtension(parts.extension);
    setFileTypeId(file.fileTypeId ?? undefined);
    setDescription(file.description || '');
  };

  const handleSave = async () => {
    try {
      toggleSaving(true);
      const invalid = validateFields(name);
      if (!isEmpty(invalid)) return setInvalidFields(invalid);
      const filename = extension.length > 0 ? `${name}.${extension}` : name;
      const data = { name: filename, description };
      const updatedFile = await API.Files.updateFile(fileId!, data);
      dispatch.addFiles([updatedFile]);
      showSuccessToaster('File updated');
      toggleSaving(false);
      if (onSave) onSave(updatedFile);
      onClose();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving file');
    }
  };

  return (
    <Dialog className={classes.dialog} onClose={onClose}>
      <DialogTitle title="Update file" onClose={onClose} />
      <DialogBody className={classes.body}>
        <TextField
          error={!isValidField('name')}
          className={classes.input}
          label="Name"
          value={name}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {formatExtension(extension)}
              </InputAdornment>
            ),
          }}
          onChange={setName}
        />
        {/*<SelectDropdown*/}
        {/*  className={classes.input}*/}
        {/*  menuClassName={classes.selectDropdown}*/}
        {/*  label="File Type"*/}
        {/*  value={fileTypeId ? getValue(fileTypeId) : null}*/}
        {/*  options={FILE_TYPE_OPTIONS}*/}
        {/*  onChange={(update) => setFileTypeId(update.value)}*/}
        {/*/>*/}
        <TextField
          multiline
          className={classes.input}
          minRows={3}
          maxRows={5}
          label="Description (optional)"
          value={description}
          onChange={setDescription}
        />
      </DialogBody>
      <DefaultDialogActions
        disabled={isDisabled(name)}
        pending={isSaving}
        confirmLabel="Save"
        pendingLabel="Saving"
        onCancel={onClose}
        onConfirm={handleSave}
      />
    </Dialog>
  );
}
